import UserAvatar from './UserAvatar';
import ConfirmModal from './ConfirmModal';
import SprintItemModal from './SprintItemModal';
import { ChevronLeftIcon, LoaderIcon, HistoryIcon, ImageIcon, FileTextIcon, DeleteIcon } from './Icons';
import { TYPE_CONFIG, COLUMNS, RETRO_COLUMNS } from '../constants';
import { formatDate, findAssignee } from '../utils';
import useSprintHistory from '../hooks/useSprintHistory';

export default function SprintHistoryView({ onBack, showToast, users, userProfile, projectId, projectType }) {
  const isRetro = projectType === 'retrospective';
  const activeColumns = isRetro ? RETRO_COLUMNS : COLUMNS;

  const {
    sprints, loading, selectedSprint, setSelectedSprint, sprintItems, loadingItems,
    exporting, deleteTarget, setDeleteTarget, showDeleteAll, setShowDeleteAll,
    isAdmin, handleSelectSprint, handleDeleteSprint, handleDeleteAll, handleExport,
    editingHistoryItem, setEditingHistoryItem, handleUpdateHistoryItem, handleDeleteHistoryItem,
  } = useSprintHistory({ showToast, projectId, userProfile });

  if (loading) {
    return (
      <div className="app-loading">
        <LoaderIcon size={32} />
        <p>Loading history...</p>
      </div>
    );
  }

  if (selectedSprint) {
    const closedByUser = findAssignee(users, selectedSprint.closedBy);

    return (
      <div className="sprint-history-wrapper">
        <div className="sprint-history-header">
          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedSprint(null)}>
            <ChevronLeftIcon size={16} /> Back
          </button>
          <div className="sprint-history-header-info">
            <h2>{selectedSprint.name}</h2>
            <div className="sprint-history-header-meta">
              <span className="sprint-history-header-dates">
                {formatDate(selectedSprint.startDate)} &ndash; {formatDate(selectedSprint.endDate)}
              </span>
              {selectedSprint.closedAt && (
                <span className="sprint-history-header-separator">&middot;</span>
              )}
              {selectedSprint.closedAt && (
                <span className="sprint-history-closed-info">
                  Ended {formatDate(selectedSprint.closedAt)}
                  {closedByUser && (
                    <> by <UserAvatar user={closedByUser} size={22} showStatus={false} /> <strong>{closedByUser.displayName}</strong></>
                  )}
                </span>
              )}
            </div>
          </div>
          <div className="sprint-history-header-actions">
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('sprint-history-board-export', 'png')} disabled={exporting || loadingItems}>
              <ImageIcon size={16} /> PNG
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('sprint-history-board-export', 'pdf')} disabled={exporting || loadingItems}>
              <FileTextIcon size={16} /> PDF
            </button>
          </div>
        </div>

        {loadingItems ? (
          <div className="app-loading">
            <LoaderIcon size={24} />
            <p>Loading items...</p>
          </div>
        ) : (
          <div className="sprint-history-board" id="sprint-history-board-export">
            {activeColumns.map((col) => {
              const colItems = sprintItems.filter((i) => i.status === col.status);
              return (
                <div key={col.status} className={`sprint-column sprint-column-${col.status}`}>
                  <div className="sprint-column-header">
                    <h3>{col.title}</h3>
                    <span className="sprint-column-count">{colItems.length}</span>
                  </div>
                  <div className="sprint-column-body">
                    {colItems.map((item) => {
                      if (isRetro) {
                        return (
                          <div
                            key={item.id}
                            className="sprint-item-card sprint-item-card--retro sprint-item-card--history-editable"
                            onClick={() => setEditingHistoryItem(item)}
                          >
                            <h4 className="sprint-item-title">{item.title}</h4>
                            {item.description && (
                              <p className="sprint-item-desc">{item.description}</p>
                            )}
                          </div>
                        );
                      }
                      const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
                      const Icon = config.icon;
                      const assignee = findAssignee(users, item.assigneeId);
                      return (
                        <div key={item.id} className="sprint-item-card">
                          <div className="sprint-item-card-header">
                            <span className="sprint-item-type" style={{ color: config.color }}>
                              <Icon size={16} /> {config.label}
                            </span>
                            {item.storyPoints != null && (
                              <span className="sprint-item-points">{item.storyPoints}</span>
                            )}
                          </div>
                          <h4 className="sprint-item-title">{item.title}</h4>
                          {item.description && (
                            <p className="sprint-item-desc">{item.description}</p>
                          )}
                          {assignee && (
                            <div className="sprint-item-assignee" style={{ marginTop: '6px' }}>
                              <UserAvatar user={assignee} size={24} showStatus={false} />
                              <span className="sprint-item-assignee-name">{assignee.displayName}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {colItems.length === 0 && (
                      <div className="sprint-column-empty">No items</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editingHistoryItem && (
          <SprintItemModal
            item={editingHistoryItem}
            onClose={() => setEditingHistoryItem(null)}
            onUpdate={handleUpdateHistoryItem}
            onDelete={handleDeleteHistoryItem}
            users={users}
            isRetro={isRetro}
            allItems={sprintItems}
          />
        )}
      </div>
    );
  }

  return (
    <div className="sprint-history-wrapper">
      <div className="sprint-history-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ChevronLeftIcon size={16} /> Back to Board
        </button>
        <h2><HistoryIcon size={20} /> {isRetro ? 'Retro History' : 'Sprint History'}</h2>
        {isAdmin && sprints.length > 0 && (
          <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteAll(true)}>
            <DeleteIcon size={14} /> Delete All
          </button>
        )}
      </div>

      {sprints.length === 0 ? (
        <div className="sprint-history-empty">
          {isRetro
            ? 'No completed retrospectives yet. Retros will appear here after they end.'
            : 'No closed sprints yet. Sprints will appear here after they end.'}
        </div>
      ) : (
        <div className="sprint-history-list">
          {sprints.map((sprint) => {
            const closedBy = findAssignee(users, sprint.closedBy);
            return (
              <div
                key={sprint.id}
                className="sprint-history-item"
                onClick={() => handleSelectSprint(sprint)}
              >
                <div className="sprint-history-item-info">
                  <h3>{sprint.name}</h3>
                  <div className="sprint-history-item-meta">
                    <span className="sprint-history-item-dates">{formatDate(sprint.startDate)} &ndash; {formatDate(sprint.endDate)}</span>
                    {sprint.closedAt && (
                      <span className="sprint-history-item-closed">
                        Ended {formatDate(sprint.closedAt)}
                        {closedBy && (
                          <>
                            {' '}by <UserAvatar user={closedBy} size={22} showStatus={false} />
                            <strong>{closedBy.displayName}</strong>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <button
                    className="btn-icon sprint-history-item-delete"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(sprint.id); }}
                    title="Delete sprint"
                  >
                    <DeleteIcon size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          message="Delete this sprint and all its items? This cannot be undone."
          onConfirm={handleDeleteSprint}
          onCancel={() => setDeleteTarget(null)}
          confirmLabel="Delete"
        />
      )}

      {showDeleteAll && (
        <ConfirmModal
          message="Delete ALL sprint history? This will permanently remove all closed sprints and their items."
          onConfirm={handleDeleteAll}
          onCancel={() => setShowDeleteAll(false)}
          confirmLabel="Delete All"
        />
      )}
    </div>
  );
}
